import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from '../../i18n';
import { api } from '../../lib/api';
import { apiErrorMessage } from '../../lib/errors';
import {
  KIND_KEY,
  PARTICIPANT_KEY,
  PHASE_KEY,
  phaseTone,
  RAIL_KEY,
  railTone,
} from '../../lib/contest';
import type { AdminQuestion, Contest, Participant, QuestionKind, Rail } from '../../types';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Alert } from '../../components/ui/alert';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { FullPageLoader } from '../../components/ui/spinner';

interface AdminContestDetail {
  contest: Contest;
  questions: AdminQuestion[];
  unkeyedQuestionIds: string[];
  participants: Participant[];
  availableTransitions: string[];
}

interface DraftForm {
  rail: Rail;
  title: string;
  asset: string;
  entryFee: string;
  sponsorFunded: string;
  registrationOpensAt: string;
  registrationClosesAt: string;
  startsAt: string;
  endsAt: string;
  paidPlaces: string;
  minParticipants: string;
}

/** `datetime-local` value for `now + offsetMs`. */
function isoLocal(offsetMs: number): string {
  return new Date(Date.now() + offsetMs).toISOString().slice(0, 16);
}

function defaultDraft(): DraftForm {
  return {
    rail: 'crypto',
    title: '',
    asset: 'USDT',
    entryFee: '0',
    sponsorFunded: '0',
    registrationOpensAt: isoLocal(60_000),
    registrationClosesAt: isoLocal(3_600_000),
    startsAt: isoLocal(3_600_000),
    endsAt: isoLocal(5_400_000),
    paidPlaces: '3',
    minParticipants: '1',
  };
}

const fieldClass =
  'h-10 rounded-lg border border-border-strong bg-surface px-3 text-sm text-fg w-full';

/** Admin contest list, creation and per-contest management. */
export default function AdminContestsPage() {
  const { id } = useParams();
  const { t } = useTranslation();
  const [contests, setContests] = useState<Contest[]>([]);
  const [detail, setDetail] = useState<AdminContestDetail | null>(null);
  const [form, setForm] = useState<DraftForm>(defaultDraft);
  // Which rails this deployment offers, so the create form cannot present a rail the
  // API will refuse. Defaults to crypto, matching the launch policy.
  const [enabledRails, setEnabledRails] = useState<Rail[]>(['crypto']);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const payload = await api.get<{ enabledRails: Rail[] }>('/contests/providers');
        if (!cancelled && Array.isArray(payload.enabledRails) && payload.enabledRails.length > 0) {
          setEnabledRails(payload.enabledRails);
          // Keep the draft's rail valid against the policy actually in force.
          setForm((current) =>
            payload.enabledRails.includes(current.rail)
              ? current
              : { ...current, rail: payload.enabledRails[0], asset: payload.enabledRails[0] === 'fiat' ? 'USD' : 'USDT' },
          );
        }
      } catch {
        // Fall back to the crypto-only default; the API refuses anything else anyway.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const load = useCallback(async () => {
    setError(null);
    try {
      const list = await api.get<{ contests: Contest[] }>('/admin/contests');
      setContests(list.contests);
      setDetail(id ? await api.get<AdminContestDetail>(`/admin/contests/${id}`) : null);
    } catch (error_) {
      setError(apiErrorMessage(error_, t));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(action: () => Promise<unknown>, doneNotice?: string): Promise<void> {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await action();
      // Prefer a specific confirmation when the caller has one: "Done." does not tell an
      // operator what just happened.
      setNotice(doneNotice ?? t('admin.contests.transitionDone'));
      await load();
    } catch (error_) {
      setError(apiErrorMessage(error_, t));
    } finally {
      setBusy(false);
    }
  }

  async function createContest(): Promise<void> {
    await run(async () => {
      await api.post('/admin/contests', {
        rail: form.rail,
        title: form.title,
        asset: form.asset,
        // Money crosses the wire as a decimal string, never a number.
        entryFee: form.entryFee,
        sponsorFunded: form.sponsorFunded,
        paidPlaces: Number.parseInt(form.paidPlaces, 10),
        minParticipants: Number.parseInt(form.minParticipants, 10),
        registrationOpensAt: new Date(form.registrationOpensAt).toISOString(),
        registrationClosesAt: new Date(form.registrationClosesAt).toISOString(),
        startsAt: new Date(form.startsAt).toISOString(),
        endsAt: new Date(form.endsAt).toISOString(),
        tieBreak: 'total_time',
      });
      setForm(defaultDraft());
    }, t('admin.contests.created'));
  }

  if (loading) {
    return <FullPageLoader label={t('common.loading')} />;
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-10">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight text-fg">
          {t('admin.contests.title')}
        </h1>
        <p className="mt-1 text-sm text-muted">{t('admin.contests.subtitle')}</p>
      </header>

      {error && (
        <div className="mt-6">
          <Alert tone="error">{error}</Alert>
        </div>
      )}
      {notice && !error && (
        <div className="mt-6">
          <Alert tone="success">{notice}</Alert>
        </div>
      )}

      <Card className="mt-8">
        <CardHeader>
          <CardTitle>{t('admin.contests.create')}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <p className="text-sm text-muted">{t('admin.contests.formHint')}</p>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="flex flex-col gap-1 sm:col-span-2">
              <Label htmlFor="new-title">{t('admin.contests.field.title')}</Label>
              <Input
                id="new-title"
                value={form.title}
                onChange={(event) => setForm({ ...form, title: event.target.value })}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="new-rail">{t('admin.contests.field.rail')}</Label>
              <select
                id="new-rail"
                className={fieldClass}
                value={form.rail}
                onChange={(event) => {
                  const rail = event.target.value as Rail;
                  setForm({ ...form, rail, asset: rail === 'fiat' ? 'USD' : 'USDT' });
                }}
              >
                {/* Only rails this deployment offers. Creating a contest on a rail that
                    is switched off is refused by the API, so offering it here would be
                    a form that cannot succeed. */}
                {enabledRails.map((rail) => (
                  <option key={rail} value={rail}>
                    {t(RAIL_KEY[rail])}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="new-asset">{t('admin.contests.field.asset')}</Label>
              <select
                id="new-asset"
                className={fieldClass}
                value={form.asset}
                onChange={(event) => setForm({ ...form, asset: event.target.value })}
              >
                {(form.rail === 'fiat' ? ['USD', 'EUR', 'GBP'] : ['USDT']).map(
                  (asset) => (
                    <option key={asset} value={asset}>
                      {asset}
                    </option>
                  ),
                )}
              </select>
            </div>

            <div className="flex flex-col gap-1">
              <Label htmlFor="new-fee">{t('admin.contests.field.entryFee')}</Label>
              <Input
                id="new-fee"
                inputMode="decimal"
                value={form.entryFee}
                onChange={(event) => setForm({ ...form, entryFee: event.target.value })}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="new-sponsor">{t('admin.contests.field.sponsor')}</Label>
              <Input
                id="new-sponsor"
                inputMode="decimal"
                value={form.sponsorFunded}
                onChange={(event) => setForm({ ...form, sponsorFunded: event.target.value })}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="new-places">{t('admin.contests.field.paidPlaces')}</Label>
              <Input
                id="new-places"
                inputMode="numeric"
                value={form.paidPlaces}
                onChange={(event) => setForm({ ...form, paidPlaces: event.target.value })}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="new-min">{t('admin.contests.field.minParticipants')}</Label>
              <Input
                id="new-min"
                inputMode="numeric"
                value={form.minParticipants}
                onChange={(event) => setForm({ ...form, minParticipants: event.target.value })}
              />
            </div>

            <div className="flex flex-col gap-1">
              <Label htmlFor="new-reg-open">{t('admin.contests.field.regOpens')}</Label>
              <Input
                id="new-reg-open"
                type="datetime-local"
                value={form.registrationOpensAt}
                onChange={(event) => setForm({ ...form, registrationOpensAt: event.target.value })}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="new-reg-close">{t('admin.contests.field.regCloses')}</Label>
              <Input
                id="new-reg-close"
                type="datetime-local"
                value={form.registrationClosesAt}
                onChange={(event) =>
                  setForm({ ...form, registrationClosesAt: event.target.value })
                }
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="new-start">{t('admin.contests.field.startsAt')}</Label>
              <Input
                id="new-start"
                type="datetime-local"
                value={form.startsAt}
                onChange={(event) => setForm({ ...form, startsAt: event.target.value })}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="new-end">{t('admin.contests.field.endsAt')}</Label>
              <Input
                id="new-end"
                type="datetime-local"
                value={form.endsAt}
                onChange={(event) => setForm({ ...form, endsAt: event.target.value })}
              />
            </div>
          </div>

          <div>
            <Button onClick={createContest} loading={busy} disabled={form.title.trim().length < 3}>
              {t('admin.contests.create')}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="mt-8">
        <CardHeader>
          <CardTitle>{t('admin.contests.title')}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {contests.length === 0 ? (
            <p className="text-sm text-muted">{t('admin.contests.empty')}</p>
          ) : (
            contests.map((contest) => (
              <div
                key={contest.id}
                className="flex flex-wrap items-center gap-2 border-b border-border pb-3 text-sm last:border-0"
              >
                <Badge tone={railTone(contest.rail)}>{t(RAIL_KEY[contest.rail])}</Badge>
                <Badge tone={phaseTone(contest.phase)}>{t(PHASE_KEY[contest.phase])}</Badge>
                <Link
                  to={`/admin/contests/${contest.id}`}
                  className="font-medium text-fg hover:underline"
                >
                  {contest.title}
                </Link>
                <span className="text-muted">
                  {contest.entryFee} {contest.asset}
                </span>
                <span className="text-muted">
                  {t('contest.card.participants', { count: contest.participantCount })}
                </span>
                {/* The public page is served only when the Worker will actually serve it:
                    it hides contests an administrator has not published, and hides
                    contests on a payment rail this deployment has switched off (both
                    answer 404 with the same "does not exist" body). Offering the link in
                    either case would be an action that can never succeed. The row title
                    above already opens the management view for every status. */}
                {contest.status === 'draft' ||
                contest.status === 'cancelled' ||
                !enabledRails.includes(contest.rail) ? (
                  <span className="ms-auto text-xs text-muted">
                    {t('admin.contests.noPublicPage')}
                  </span>
                ) : (
                  <Link to={`/contests/${contest.id}`} className="ms-auto">
                    <Button variant="ghost" size="sm">
                      {t('contest.list.view')}
                    </Button>
                  </Link>
                )}
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {detail && (
        <Card className="mt-8">
          <CardHeader>
            <CardTitle>{t('admin.contests.transitions')}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone={phaseTone(detail.contest.phase)}>
                {t(PHASE_KEY[detail.contest.phase])}
              </Badge>
              <span className="text-sm font-medium text-fg">{detail.contest.title}</span>
              <span className="text-sm text-muted">
                {t('admin.contests.prizePoolLabel')}: {detail.contest.prizePool}{' '}
                {detail.contest.asset}
              </span>
              {/* The platform's 30% share, frozen beside the pool at finalization. */}
              <span className="text-sm text-muted">
                {t('admin.contests.platformFeeLabel')}: {detail.contest.platformFee}{' '}
                {detail.contest.asset}
              </span>
            </div>

            {detail.unkeyedQuestionIds.length > 0 && (
              <Alert tone="error">{t('admin.contests.unkeyed')}</Alert>
            )}

            <div className="flex flex-wrap gap-2">
              <Button
                loading={busy}
                disabled={detail.contest.status !== 'draft'}
                onClick={() =>
                  void run(() =>
                    api.post(`/admin/contests/${detail.contest.id}/transition`, {
                      action: 'schedule',
                    }),
                  )
                }
              >
                {t('admin.contests.action.schedule')}
              </Button>
              <Button
                variant="outline"
                loading={busy}
                disabled={
                  detail.contest.status !== 'active' && detail.contest.status !== 'finalizing'
                }
                onClick={() =>
                  void run(() =>
                    api.post(`/admin/contests/${detail.contest.id}/transition`, {
                      action: 'finalize',
                    }),
                  )
                }
              >
                {t('admin.contests.action.finalize')}
              </Button>
              <Button
                variant="outline"
                loading={busy}
                disabled={detail.contest.status !== 'finalized'}
                onClick={() =>
                  void run(() =>
                    api.post(`/admin/contests/${detail.contest.id}/transition`, {
                      action: 'publish',
                    }),
                  )
                }
              >
                {t('admin.contests.action.publish')}
              </Button>
              <Button
                variant="danger"
                loading={busy}
                disabled={detail.contest.status === 'published'}
                onClick={() =>
                  void run(() =>
                    api.post(`/admin/contests/${detail.contest.id}/transition`, {
                      action: 'cancel',
                      reason: 'cancelled by administrator',
                    }),
                  )
                }
              >
                {t('admin.contests.action.cancel')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {detail && (
        <QuestionAuthor contestId={detail.contest.id} detail={detail} busy={busy} onRun={run} />
      )}

      {detail && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>{t('admin.contests.participants')}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            {detail.participants.length === 0 ? (
              <p className="text-sm text-muted">{t('common.none')}</p>
            ) : (
              detail.participants.map((participant) => (
                <div
                  key={participant.id}
                  className="flex flex-wrap items-center gap-2 border-b border-border pb-3 text-sm last:border-0"
                >
                  <Badge tone="neutral">{t(PARTICIPANT_KEY[participant.status])}</Badge>
                  <span className="text-fg">
                    {participant.email ?? participant.displayName ?? participant.id}
                  </span>
                  <span className="text-muted">
                    {t('contest.results.score')}: {participant.score}
                  </span>
                  <span className="text-muted">
                    {t('contest.results.prize')}: {participant.payout}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="ms-auto"
                    loading={busy}
                    onClick={() =>
                      void run(() =>
                        api.post(
                          `/admin/contests/${detail.contest.id}/participants/${participant.id}/disqualify`,
                          { reason: 'disqualified by administrator' },
                        ),
                      )
                    }
                  >
                    {t('admin.contests.disqualify')}
                  </Button>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/**
 * Question authoring.
 *
 * The answer key is written here and never leaves the server: a participant only
 * ever receives the prompt and the option labels.
 */
function QuestionAuthor({
  contestId,
  detail,
  busy,
  onRun,
}: {
  contestId: string;
  detail: AdminContestDetail;
  busy: boolean;
  onRun: (action: () => Promise<unknown>) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [kind, setKind] = useState<QuestionKind>('single_choice');
  const [prompt, setPrompt] = useState('');
  const [points, setPoints] = useState('1000');
  const [options, setOptions] = useState(['', '', '']);
  const [correctIndex, setCorrectIndex] = useState(0);
  const [numericAnswer, setNumericAnswer] = useState('');

  const editable = ['draft', 'scheduled', 'registration'].includes(detail.contest.status);

  async function submit(): Promise<void> {
    await onRun(async () => {
      const body: Record<string, unknown> = {
        kind,
        prompt,
        points: Number.parseInt(points, 10),
      };
      if (kind === 'numeric') {
        body.numericAnswer = numericAnswer;
      } else {
        body.options = options
          .filter((label) => label.trim().length > 0)
          .map((label, index) => ({ label, isCorrect: index === correctIndex }));
      }
      await api.post(`/admin/contests/${contestId}/questions`, body);
      setPrompt('');
      setOptions(['', '', '']);
      setNumericAnswer('');
    });
  }

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle>{t('admin.contests.questions')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {detail.questions.length === 0 ? (
          <p className="text-sm text-muted">{t('admin.contests.noQuestions')}</p>
        ) : (
          detail.questions.map((question) => (
            <div key={question.id} className="flex flex-col gap-1 border-b border-border pb-3">
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted">
                <span>{t(KIND_KEY[question.kind])}</span>
                <span>
                  {t('admin.contests.points')}: {question.points}
                </span>
                {detail.unkeyedQuestionIds.includes(question.id) && (
                  <Badge tone="danger">{t('admin.contests.answerKey')}</Badge>
                )}
              </div>
              <p className="text-sm font-medium text-fg">{question.prompt}</p>
              <p className="text-xs text-muted">
                {t('admin.contests.answerKey')}:{' '}
                {question.kind === 'numeric'
                  ? (question.numericAnswer ?? t('common.none'))
                  : question.options
                      .filter((option) => question.correctOptionIds.includes(option.id))
                      .map((option) => option.label)
                      .join(', ') || t('common.none')}
              </p>
              <div>
                <Button
                  variant="ghost"
                  size="sm"
                  loading={busy}
                  onClick={() =>
                    void onRun(() =>
                      api.delete(`/admin/contests/${contestId}/questions/${question.id}`),
                    )
                  }
                >
                  {t('admin.contests.deleteQuestion')}
                </Button>
              </div>
            </div>
          ))
        )}

        {editable && (
          <div className="flex flex-col gap-3 border-t border-border pt-4">
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="flex flex-col gap-1">
                <Label htmlFor="q-kind">{t('admin.contests.questionKind')}</Label>
                <select
                  id="q-kind"
                  className={fieldClass}
                  value={kind}
                  onChange={(event) => setKind(event.target.value as QuestionKind)}
                >
                  {(['single_choice', 'multi_choice', 'true_false', 'numeric'] as const).map(
                    (value) => (
                      <option key={value} value={value}>
                        {t(KIND_KEY[value])}
                      </option>
                    ),
                  )}
                </select>
              </div>
              <div className="flex flex-col gap-1 sm:col-span-2">
                <Label htmlFor="q-prompt">{t('admin.contests.prompt')}</Label>
                <Input
                  id="q-prompt"
                  value={prompt}
                  onChange={(event) => setPrompt(event.target.value)}
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label htmlFor="q-points">{t('admin.contests.points')}</Label>
                <Input
                  id="q-points"
                  inputMode="numeric"
                  value={points}
                  onChange={(event) => setPoints(event.target.value)}
                />
              </div>
            </div>

            {kind === 'numeric' ? (
              <div className="flex flex-col gap-1">
                <Label htmlFor="q-numeric">{t('admin.contests.numericAnswer')}</Label>
                <Input
                  id="q-numeric"
                  inputMode="decimal"
                  value={numericAnswer}
                  onChange={(event) => setNumericAnswer(event.target.value)}
                />
              </div>
            ) : (
              <fieldset className="flex flex-col gap-2">
                <legend className="text-sm font-medium text-fg">
                  {t('admin.contests.options')}
                </legend>
                {options.map((label, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <Input
                      aria-label={t('admin.contests.optionLabel', { n: index + 1 })}
                      value={label}
                      onChange={(event) => {
                        const next = [...options];
                        next[index] = event.target.value;
                        setOptions(next);
                      }}
                    />
                    <label className="flex shrink-0 items-center gap-2 text-sm text-muted">
                      <input
                        type="radio"
                        name="correct-option"
                        checked={correctIndex === index}
                        onChange={() => setCorrectIndex(index)}
                        className="h-4 w-4 text-brand"
                      />
                      {t('admin.contests.correct')}
                    </label>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setOptions([...options, ''])}
                >
                  + {t('admin.contests.optionLabel', { n: options.length + 1 })}
                </Button>
              </fieldset>
            )}

            <div>
              <Button onClick={submit} loading={busy} disabled={prompt.trim().length === 0}>
                {t('admin.contests.saveQuestion')}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

